import type { MissionScript } from './runner';
import type { TargetRef } from './types';

/**
 * Bits the job definitions share: control wording that follows the device, and a motor left for a
 * player who is on foot when a getaway starts.
 *
 * The HUD rewords "press E" for touch on its own (src/ui/format.ts); the other controls a job may
 * name — holding E, swinging, picking a weapon — are written here as functions of `touch`, which the
 * runner resolves when it builds the objective (StageBase.hint).
 */

/** "<lead> Hold E." on a keyboard, "<lead> Hold USE." on touch. */
export const holdHint =
  (lead: string): ((touch: boolean) => string) =>
  (touch) =>
    `${lead} ${touch ? 'Hold USE.' : 'Hold E.'}`;

/** The swing / fire control, for a sentence ("swing (Q or left click)"). */
export const swingKey = (touch: boolean): string => (touch ? 'HIT' : 'Q or left click');

/** The next-weapon control. */
export const weaponKey = (touch: boolean): string => (touch ? 'SWAP' : '1 or the mouse wheel');

/** True when a car the player could drive off in stands within `radius` m of them. */
export function motorNearby(m: MissionScript, radius = 25): boolean {
  const player = m.ctx.services.get('player');
  if (player.mode === 'driving') return true;
  return m.ctx.services
    .get('vehicles')
    .query(player.pos, radius)
    .some((v) => !v.destroyed && v.health / v.spec.maxHealth > 0.3 && v.spec.id !== 'police' && !v.tags.has('police'));
}

/**
 * A getaway starts and the player came on foot: leave an ordinary motor at the kerb nearby, released
 * at once (it isn't the job's car, so wrecking or leaving it means nothing). Nothing happens when a
 * drivable car is already in reach.
 */
export function leaveAMotor(m: MissionScript, near: TargetRef, spec = 'compact'): void {
  if (motorNearby(m)) return;
  m.spawnVehicle('getaway', spec, near);
  m.releaseVehicle('getaway');
}
